/** @file thinSolids: one solid mesh drawn as N thin instances (one draw call) with per-instance colours. */
import '@babylonjs/core/Meshes/thinInstanceMesh.js';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial.js';
import { Color3 } from '@babylonjs/core/Maths/math.color.js';
import type { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode.js';
import { VIEWPORT_LAYERS } from './viewportLayers';

/**
 * The instanced mesh; `matrices` (16 floats per instance, Babylon row-vector order: the images of local x, y, z,
 * then the position) and `paint(i, color)` (a shared Color3, compared by identity) are written in place;
 * `markMatrices()` flags the matrices as changed and `commit()` uploads what changed.
 */
export type ThinSolids = { mesh: Mesh; matrices: Float32Array; markMatrices(): void; paint(i: number, c: Color3): void; commit(): void };

/** The unlit material: `disableLighting` keeps only the emissive term (white), times the instance colour. */
function solidMaterial(mesh: Mesh, alpha: number, blend: boolean): StandardMaterial {
  const m = new StandardMaterial(`${mesh.name}-material`, mesh.getScene());
  Object.assign(m, { disableLighting: true, diffuseColor: Color3.Black(), emissiveColor: Color3.White(), specularColor: Color3.Black(), backFaceCulling: false, alpha });
  if (blend) m.disableDepthWrite = true;
  return m;
}

/**
 * `mesh` (any builder's output) under `parent` with `count` identity instances on the overlay layer, an unlit
 * material at `alpha`, both faces drawn, rendering group `group`, never picked or culled.
 */
export function thinSolids(mesh: Mesh, parent: TransformNode | null, count: number, alpha: number, group: number): ThinSolids {
  const material = solidMaterial(mesh, alpha, alpha < 1);
  Object.assign(mesh, { material, parent, layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: group, isPickable: false, alwaysSelectAsActiveMesh: true });
  const matrices = new Float32Array(16 * count);
  for (let i = 0; i < count; i++) for (const k of [0, 5, 10, 15]) matrices[16 * i + k] = 1;
  const colors = new Float32Array(4 * count).fill(1);
  mesh.thinInstanceSetBuffer('matrix', matrices, 16, false);
  mesh.thinInstanceSetBuffer('color', colors, 4, false);
  const painted: (Color3 | null)[] = Array.from({ length: count }, () => null);
  const dirty = { matrix: false, color: false };
  return {
    mesh, matrices,
    markMatrices: () => { dirty.matrix = true; },
    paint: (i, c) => {
      if (painted[i] === c) return;
      painted[i] = c;
      colors[4 * i] = c.r;
      colors[4 * i + 1] = c.g;
      colors[4 * i + 2] = c.b;
      dirty.color = true;
    },
    commit: () => {
      if (dirty.matrix) mesh.thinInstanceBufferUpdated('matrix');
      if (dirty.color) mesh.thinInstanceBufferUpdated('color');
      dirty.matrix = false;
      dirty.color = false;
    },
  };
}
